import { Grid, FormControlLabel, Checkbox, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../../hooks";
import QTDetailsImageWithLabelSection from "../../../../../common/QTDetailsImageWithLabelSection";
import QTDetailsAudioWithLabelSection from "../../../../../common/QTDetailsAudioWithLabelSection";
import QTDetailsTextFieldWithLabelSection from "../../../../../common/QTDetailsTextFieldWithLabelSection";
import { useContributorsContext } from "../../../../../providers/OrganizationAdmin/ContributorsProvider";
import QTDetailsAutoCompleteWithLabelSection from "../../../../../common/QTDetailsAutoCompleteWithLabelSection";
import CreatedByPickerModal from "../CreatedByPickerModal/CreatedByPickerModal";
import { getCreatedByDisplayFromQT } from "../../../../../utils/createdByUtils";
import {
  capitalizeFirstLetterOfEveryWord,
  images,
} from "../../../../../constants";

import QTDetailsLocationWithLabelSection from "../../../../../common/QTDetailsLocationWithLabelSection";
import QTDetailsLatLongSection from "../../../../../common/QTDetailsLatLongSection";

import moment from "moment";
import { getCustomNarratorsByUser } from "../../../../../modules/OrganizationQuickTales/hooks/CustomNarratorsHooks";
import { set } from "date-fns";

const QTDetailsDataFieldsSection = ({
  quicktaleDetails,
  setQuicktaleDetails,
  handleImagePreviewModalOpen,
  handleAudioModalOpen,
  handleProcessedAudioModalOpen,
  editEnabled,
  uploadImage,
  setUploadImage,
  uploadAudioFile,
  setUploadAudioFile,
  uploadProcessedAudioFile,
  setUploadProcessedAudioFile,
  handleFileUpload,
  onProcessOriginalAudio,
  onConvertImageToJpg,
  convertingImage = false,
  isCreateMode = false,
  isSaving,
  narratorError,
  organizationsData = [],
}) => {
  const { currentUser } = useAuth();
  const { GAUsers, handleGetAllGAUsers } = useContributorsContext();
  const [usersData, setUsersData] = useState([]);
  const [customNarrators, setCustomNarrators] = useState([]);
  const [mergedNarrators, setMergedNarrators] = useState([]);
  const [isDraftEnabled, setisDraftEnabled] = useState(true);
  const [createdByPickerOpen, setCreatedByPickerOpen] = useState(false);

  const handleCreatedBySelect = (item) => {

    setQuicktaleDetails({
      ...quicktaleDetails,
      createdByData: item,
      createdBy: item.id,
    });
  };

  // Auto-switch isDraft status based on required fields
  useEffect(() => {
    if (isSaving?.current) {
      // If we are saving, don't run this effect.
      // Reset the flag after a short delay to allow the save to complete.
      setTimeout(() => {
        isSaving.current = false;
      }, 100);
      return;
    }

    const hasAudio = !!(uploadAudioFile || quicktaleDetails.audioUrl);
    const hasImage = !!(uploadImage || quicktaleDetails.imageUrls?.[0]);
    const hasTitle = !!quicktaleDetails.title && quicktaleDetails.title.trim().length > 0;
    
    if (hasAudio && hasImage && hasTitle) {
      // Auto-switch to Finished when all three are present
      if (quicktaleDetails.isDraft !== false) {
        setQuicktaleDetails({
          ...quicktaleDetails,
          isDraft: false,
        });
        setisDraftEnabled(true);
      }
    } else {
      // Auto-switch to Draft when any element is missing
      //if (quicktaleDetails.isDraft !== true) {
        setQuicktaleDetails({
          ...quicktaleDetails,
          isDraft: true,
        });
        setisDraftEnabled(false);
      //}
    }
  }, [
    quicktaleDetails.audioName,
    quicktaleDetails.audioUrl,
    quicktaleDetails.photoUrl,
    quicktaleDetails.imageUrls,
    quicktaleDetails.title,
    uploadImage,
    uploadAudioFile,
  ]);

  useEffect(() => {
    if (GAUsers && GAUsers?.length > 0) {
    } else {
      handleGetAllGAUsers();
    }
  }, []);

  useEffect(() => {
    // Apply the same filtering and sorting logic that's in the provider
    let filteredUsers = GAUsers.filter(user => user !== null);

    // Filter out "Guest User" entries (first name + last name combined)
    filteredUsers = filteredUsers.filter(user => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return fullName !== "Guest User";
    });

    // Sort alphabetically by full name (case-insensitive)
    filteredUsers.sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase().trim();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });

    setUsersData(filteredUsers);
  }, [GAUsers]);

  // Fetch custom narrators for the current user
  useEffect(() => {
    const fetchCustomNarrators = async () => {
      // Use userId from admin record if available, otherwise fall back to uid
      const userIdToUse = currentUser?.userId || currentUser?.uid;
      if (userIdToUse) {
        const narrators = await getCustomNarratorsByUser(userIdToUse);
        setCustomNarrators(narrators);
      }
    };

    fetchCustomNarrators();
  }, [currentUser]);

  // Refresh custom narrators when entering edit mode
  useEffect(() => {
    if (editEnabled) {
      const refreshCustomNarrators = async () => {
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        if (userIdToUse) {
          const narrators = await getCustomNarratorsByUser(userIdToUse);
          setCustomNarrators(narrators);
        }
      };
      refreshCustomNarrators();
    }
  }, [editEnabled, currentUser]);

  // Merge users and custom narrators, then sort alphabetically
  useEffect(() => {
    // Transform custom narrators to match the user data structure
    const transformedCustomNarrators = customNarrators.map(narrator => ({
      id: null, // Custom narrators don't have user IDs
      fullName: narrator.name,
      // No firstName, lastName, or userName fields - this distinguishes them from users
    }));

    // Merge users and custom narrators
    const merged = [...usersData, ...transformedCustomNarrators];

    // Sort alphabetically by display name (case-insensitive)
    merged.sort((a, b) => {
      const nameA = a.firstName && a.lastName 
        ? `${a.firstName} ${a.lastName}`.toLowerCase().trim()
        : (a.fullName || '').toLowerCase().trim();
      const nameB = b.firstName && b.lastName 
        ? `${b.firstName} ${b.lastName}`.toLowerCase().trim()
        : (b.fullName || '').toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });

    setMergedNarrators(merged);
  }, [usersData, customNarrators]);

  return (
    <Grid container style={dataContainerStyles}>
      {/*left side column*/}
      <Grid item container direction={"column"} xs>
        {/*photo section*/}
        <QTDetailsImageWithLabelSection
          label={"QT Photo"}
          image={
            quicktaleDetails?.photoUrl
              ? quicktaleDetails?.photoUrl
              : quicktaleDetails?.imageUrls?.[0]
              ? quicktaleDetails?.imageUrls[0]
              : isCreateMode
              ? null
              : images.QuicktaleLogo
          }
          // image={images.tableDummyImage}
          fileName={
            quicktaleDetails?.imageFileNames?.[0] || 
            quicktaleDetails?.originalPhotoName
          }
          handleImagePreviewModalOpen={handleImagePreviewModalOpen}
          editEnabled={editEnabled}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadImage={uploadImage}
          setUploadImage={setUploadImage}
          autoOpenCropOnUpload={true}
          isCreateMode={isCreateMode}
          showConvertToJpgButton={!isCreateMode}
          onConvertToJpg={onConvertImageToJpg}
          convertingImage={convertingImage}
        />
        {/*original audio section*/}
        <QTDetailsAudioWithLabelSection
          label={`Original Audio File${!quicktaleDetails.processedAudioUrl ? '*' : ''}`}
          handleAudioModalOpen={handleAudioModalOpen}
          editEnabled={editEnabled}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadAudioFile={uploadAudioFile}
          setUploadAudioFile={setUploadAudioFile}
          handleFileUpload={(file) => handleFileUpload(file, "original")}
          showProcessButton={true}
          onProcessAudio={onProcessOriginalAudio}
        />
        {/*processed audio section*/}
        <QTDetailsAudioWithLabelSection
          label={`Processed Audio File${quicktaleDetails.processedAudioUrl ? '*' : ''}`}
          handleAudioModalOpen={handleProcessedAudioModalOpen}
          editEnabled={editEnabled}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadAudioFile={uploadProcessedAudioFile}
          setUploadAudioFile={setUploadProcessedAudioFile}
          audioNameField="processedAudioName"
          audioUrlField="processedAudioUrl"
          hideUploadButton={true}
          handleFileUpload={(file) => handleFileUpload(file, "processed")}
        />
        {/*QT ID section - hidden in create mode*/}
        {!isCreateMode && (
          <QTDetailsTextFieldWithLabelSection
            label={"QT ID"}
            value={quicktaleDetails.id}
            disabled={true}
          />
        )}
        {/*Creation Date Section - hidden in create mode*/}
        {!isCreateMode && (
          <QTDetailsTextFieldWithLabelSection
            label={"Creation Date"}
            value={moment.unix(quicktaleDetails?.createdAt.seconds).format("MM/DD/YYYY HH:mm:ss")}
            disabled={true}
          />
        )}
        {/*QT Title section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Title"}
          value={quicktaleDetails.title}
          rows={3}
          multiline={true}
          disabled={true}
          editEnabled={editEnabled}
          capitalizeWords
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              title: e.target.value,
              // title: e.target.value,
            })
          }
        />
        {/*Description section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Description"}
          value={quicktaleDetails.description}
          disabled={true}
          multiline={true}
          rows={5}
          editEnabled={editEnabled}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              description: e.target.value,
            })
          }
        />
        {/*QT_priority section (per-quicktale boost)*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Priority"}
          value={quicktaleDetails.qtPriority ?? quicktaleDetails.orgPriority}
          disabled={true}
          type="number"
          editEnabled={editEnabled}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              qtPriority: e.target.value,
            })
          }
        />
        <Grid item style={{ marginTop: -15, paddingLeft: '2%' }}>
          <span style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}>
            *This is the audio that will be played in the app.
          </span>
        </Grid>
      </Grid>
      {/*right side column*/}
      <Grid item container direction={"column"} xs>
        {/*Created By section — read-only display with a "Change" button (edit
            mode) that opens a tabbed Users/Organizations picker. The stored
            createdBy can be a users.id OR an organizations.id.*/}
        <Grid container alignItems={"center"} style={{ marginBottom: 24 }}>
          <span style={createdByLabelStyle}>Created By</span>
          <div style={createdByRowStyle}>
            <div style={createdByValueStyle}>
              {(() => {
                // Prefer the resolved createdByData. If it's missing (e.g. the
                // org lookup was blocked or hadn't loaded yet), fall back to
                // matching the raw createdBy id against the loaded org list.
                if (!quicktaleDetails?.createdByData && quicktaleDetails?.createdBy) {
                  const org = organizationsData?.find(
                    (o) => o?.id === quicktaleDetails.createdBy
                  );
                  if (org?.name) return org.name;
                }
                return getCreatedByDisplayFromQT(quicktaleDetails);
              })()}
            </div>


            {editEnabled && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCreatedByPickerOpen(true)}
                sx={{
                  textTransform: "none",
                  borderColor: "#00A7CD",
                  color: "#00A7CD",
                  whiteSpace: "nowrap",
                  fontFamily: "Avenir Book, sans-serif",
                }}
              >
                Change
              </Button>
            )}
          </div>
        </Grid>

        <CreatedByPickerModal
          open={createdByPickerOpen}
          onClose={() => setCreatedByPickerOpen(false)}
          usersData={usersData}
          organizationsData={organizationsData}
          onSelect={handleCreatedBySelect}
        />

        {/*Told By section*/}
        <QTDetailsAutoCompleteWithLabelSection
          label={"Told By*"}
          value={
            quicktaleDetails?.narratorData != null
              ? (() => {
                  const displayName = `${quicktaleDetails?.narratorData?.firstName} ${quicktaleDetails?.narratorData?.lastName}`;
                  return quicktaleDetails?.narratorData?.userName 
                    ? `${displayName}/@${quicktaleDetails?.narratorData?.userName}`
                    : displayName;
                })()
              : quicktaleDetails?.narrator || ""
          }
          onChange={(docData) => {
            console.log("data:::", docData);
            if (docData.id) {
              setQuicktaleDetails({
                ...quicktaleDetails,
                narratorData: docData,
                narrator: docData.id,
                // narrator: `${docData.firstName} ${docData.lastName}`,
              });
            } else if (docData.id == null) {
              setQuicktaleDetails({
                ...quicktaleDetails,
                narratorData: null,
                narrator: docData.fullName,
              });
            }
          }}
          disabled={true}
          optionsData={mergedNarrators}
          editEnabled={editEnabled}
          error={narratorError}
        />
        {/* <QTDetailsTextFieldWithLabelSection
          label={"Told By"}
          value={
            quicktaleDetails.narratorData
              ? quicktaleDetails.narratorData?.userName
              : quicktaleDetails.narrator
          }
          disabled={true}
        /> */}
        {/*who Can Listen section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Who Can Listen"}
          value={quicktaleDetails.status}
          disabled={true}
          editEnabled={editEnabled}
          select={true}
          dropdownValues={["public", "private", "unlisted"]}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              status: e.target.value,
            })
          }
        />
        {/*tags section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Tags(Comma separated)"}
          value={quicktaleDetails.tags}
          disabled={true}
          editEnabled={editEnabled}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              tags: e.target.value,
            })
          }
        />
        {/*location section*/}
        <QTDetailsLocationWithLabelSection
          label={"Location"}
          value={quicktaleDetails?.location}
          locationDisplayValue={quicktaleDetails?.locationDisplay}
          quicktaleDetails={quicktaleDetails}
          disabled={true}
          editEnabled={editEnabled}
          onChange={(docData) => {
            console.log("doc data::", docData);
            setQuicktaleDetails({
              ...quicktaleDetails,
              coordinates: docData.coordinates,
              location: docData.location,
              locationDisplay: docData.locationDisplay,
              locationCountry: docData.locationCountry,
              g: docData.g,
            });
            // setQuicktaleDetails({ ...quicktaleDetails, ...docData });
          }}
          // onChange={(docData) => {
          //   console.log("doc data::", docData);
          //   setQuicktaleDetails({ ...quicktaleDetails, ...docData });
          // }}
        />
        {/* <QTDetailsTextFieldWithLabelSection
          label={"Location"}
          value={quicktaleDetails?.location}
          disabled={true}
        /> */}
        {/*Geo Hash section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Geohash"}
          value={quicktaleDetails?.g?.geohash}
          disabled={true}
        />
        {/*location Country section*/}
        {/* <QTDetailsTextFieldWithLabelSection
          label={"Location Country"}
          value={quicktaleDetails?.locationCountry}
          disabled={true}
        /> */}
        {/*Lat/long section*/}
        {/* {editEnabled ? (
          <QTDetailsLatLongSection
            label={"Lat/Long"}
            // quicktaleDetails={quicktaleDetails}
            latValue={quicktaleDetails?.coordinates?._lat}
            longValue={quicktaleDetails?.coordinates?._long}
            onUpdate={(data) => {
              setQuicktaleDetails({
                ...quicktaleDetails,
                coordinates: data.coordinates,
                location: data.location,
                g: data.g,
              });
            }}
            disabled={true}
            editEnabled={editEnabled}
          />
        ) : (
          <QTDetailsTextFieldWithLabelSection
            label={"Lat/Long"}
            value={
              quicktaleDetails?.coordinates?._lat +
              "," +
              quicktaleDetails?.coordinates?._long
            }
            disabled={true}
          />
        )} */}

        {/* <QTDetailsLatLongSection
          label={"Lat/Long"}
          // quicktaleDetails={quicktaleDetails}
          latValue={quicktaleDetails?.coordinates?._lat}
          longValue={quicktaleDetails?.coordinates?._long}
          onUpdate={(data) => {
            setQuicktaleDetails({
              ...quicktaleDetails,
              coordinates: data.coordinates,
              location: data.location,
              g: data.g,
            });
          }}
          disabled={true}
          editEnabled={editEnabled}
        /> */}
        <QTDetailsLatLongSection
          label={"Lat/Long"}
          // quicktaleDetails={quicktaleDetails}
          value={
            quicktaleDetails?.coordinates?._lat +
            "," +
            quicktaleDetails?.coordinates?._long
          }
          latValue={quicktaleDetails?.coordinates?._lat}
          longValue={quicktaleDetails?.coordinates?._long}
          onChange={(data) => {
            setQuicktaleDetails({
              ...quicktaleDetails,
              coordinates: data.coordinates,
              location: data.location,
              locationDisplay: data.locationDisplay,
              locationCountry: data.locationCountry,
              g: data.g,
            });
          }}
          disabled={true}
          editEnabled={editEnabled}
        />
        {/*QT Status section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Status"}
          value={quicktaleDetails?.isDraft === true ? "Draft" : "Finished"}
          disabled={editEnabled && isDraftEnabled ? false : true}
          editEnabled={editEnabled === true ? isDraftEnabled : false}
          select={true}
          dropdownValues={["Draft", "Finished"]}
          onChange={(e) => {
            setQuicktaleDetails({
              ...quicktaleDetails,
              isDraft: e.target.value === "Draft" ? true : false,
            });
          }}
        />
      </Grid>
    </Grid>
  );
};

export default QTDetailsDataFieldsSection;

const dataContainerStyles = {
  borderRadius: 12,
  boxShadow: " 0px 0px 30px 0px rgba(80, 85, 136, 0.15)",
  background: "var(--gray-99, #FCFCFC)",
  padding: "36px 15px",
};

const createdByLabelStyle = {
  width: "25%",
  color: "#020303",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
};

const createdByRowStyle = {
  width: "70%",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const createdByValueStyle = {
  flex: 1,
  color: "#515563",
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "Avenir Book, sans-serif",
  wordBreak: "break-word",
};
